import { AlertTriangle, CircleCheck, Settings } from 'lucide-react';
import { differenceInDays, startOfDay } from 'date-fns';
import { useTranslation, Trans } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { useApiAdminlogicsworkspacesGetCollection } from '@r/api/generated/client.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isRenewableLogicType } from '@admin/types/workspace/logic.ts';
import { ActionBanner } from '@admin/components/banner/action-banner.tsx';

interface ProgramExpirationBannerProps {
  buttonText?: string;
  onButtonClick?: () => void;
  buttonTo?: string;
}

/*
 * Composant réutilisable qui affiche un encart violet avec le nombre de jours restants
 * avant la fin du programme et un bouton pour accéder aux réglages.
 */
export const ProgramExpirationBanner = ({
  buttonText,
  onButtonClick,
  buttonTo = '/settings'
}: ProgramExpirationBannerProps = {}) => {
  const { t } = useTranslation();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const currentLogicWorkspace = workspaces.workspace?.currentLogicWorkspace;
  const now = startOfDay(new Date());
  const logicWorkspacesQuery = useApiAdminlogicsworkspacesGetCollection(
    {
      'order[startAt]': 'asc'
    },
    {
      query: {
        enabled: Boolean(user)
      }
    }
  );

  if (
    workspaces.isWorkspacePending ||
    !currentLogicWorkspace ||
    !isRenewableLogicType(currentLogicWorkspace.logic?.type)
  ) {
    return null;
  }

  const endDate = startOfDay(new Date(currentLogicWorkspace.endAt));
  const daysRemaining = differenceInDays(endDate, now);
  const nextPlannedLogicWorkspace = logicWorkspacesQuery.data?.find((logicWorkspace) => {
    if (!isRenewableLogicType(logicWorkspace.logic?.type)) return false;

    return startOfDay(new Date(logicWorkspace.startAt)).getTime() > now.getTime();
  });
  const hasPendingRenewal = Boolean(nextPlannedLogicWorkspace);

  // Ne s'affiche que si il reste 60 jours ou moins (et que la date n'est pas déjà passée)
  if (!hasPendingRenewal && (daysRemaining < 0 || daysRemaining > 60)) {
    return null;
  }

  const label = buttonText ?? t('settings.renewal.banner.button.settings');

  return (
    <ActionBanner
      icon={hasPendingRenewal ? CircleCheck : AlertTriangle}
      action={
        hasPendingRenewal
          ? undefined
          : { label, icon: Settings, to: buttonTo, onClick: onButtonClick }
      }
    >
      {hasPendingRenewal ? (
        <p className="text-sm font-bold leading-relaxed">{t('settings.renewal.banner.pending')}</p>
      ) : (
        <>
          <p className="text-sm leading-relaxed">
            <Trans
              i18nKey="settings.renewal.banner.line1"
              values={{ count: daysRemaining }}
              components={{ strong: <strong className="font-semibold" /> }}
            />
          </p>
          <p className="text-sm leading-relaxed">{t('settings.renewal.banner.line2')}</p>
        </>
      )}
    </ActionBanner>
  );
};
